import { Alert, Button, Card, CardBody, CardHeader, Divider, Input, Tab, Tabs } from '@heroui/react';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, RefreshCw, Trash2 } from 'lucide-react';
import React, { FC, useEffect, useMemo, useState } from 'react';

import { ContentLoadError, PageTitle, PageWrapper, Spinner } from '../../../components';
import { useHeroToast } from '../../../hooks';
import {
  AI_PRICES_QUERY_KEY,
  AiMargins,
  AiPrices,
  MARGIN_LABELS,
  REALTIME_PRICE_FIELDS,
  useGetAiPrices,
  useUpdateAiPrices,
} from '../../../queries';

/**
 * Globalny cennik AI: stawki dostawców i marża, po których liczone jest zużycie
 * wszystkich organizacji.
 *
 * Wartości to REALNY koszt dostawcy, bez marży, dokładnie tak jak w jego cenniku.
 * Marża jest osobno, żeby po podwyżce u dostawcy dało się odróżnić koszt od narzutu.
 *
 * Formularz wysyła cennik w całości, dlatego usunięcie modelu z listy faktycznie
 * usuwa go z bazy. Dla rozmów live model bez ceny oznacza wyłączony kanał, i tak
 * ma być: bez stawki nie da się kontrolować kosztu minuty połączenia.
 */

/** Formularz trzyma stringi, bo pole w trakcie pisania bywa puste albo z przecinkiem. */
type RealtimeRow = { model: string; values: Record<string, string> };
type ChatRow = { model: string; input: string; output: string };
type PerMinuteRow = { model: string; usdPerMin: string };

const toNumber = (value: string): number => Number(String(value).replace(',', '.'));

const isValidPrice = (value: string): boolean => {
  const parsed = toNumber(value);
  return value.trim() !== '' && Number.isFinite(parsed) && parsed >= 0;
};

const isValidMargin = (value: string): boolean => {
  const parsed = toNumber(value);
  return Number.isFinite(parsed) && parsed >= 1 && parsed <= 5;
};

interface SectionProps {
  title: string;
  description: string;
  onAdd: () => void;
  children: React.ReactNode;
}

const Section: FC<SectionProps> = ({ title, description, onAdd, children }) => (
  <Card shadow="sm">
    <CardHeader className="flex items-start justify-between gap-4">
      <div>
        <p className="text-medium font-semibold">{title}</p>
        <p className="text-tiny text-default-500 mt-1 max-w-[620px]">{description}</p>
      </div>
      <Button size="sm" variant="flat" startContent={<Plus size={14} />} onPress={onAdd}>
        Dodaj model
      </Button>
    </CardHeader>
    <Divider />
    <CardBody className="gap-4">{children}</CardBody>
  </Card>
);

export const AiPricingView: FC = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useHeroToast();

  const { data, isLoading, error, refetch } = useGetAiPrices();
  const { mutateAsync: updatePrices, isPending: isSaving } = useUpdateAiPrices();

  const [realtime, setRealtime] = useState<RealtimeRow[]>([]);
  const [chat, setChat] = useState<ChatRow[]>([]);
  const [tts, setTts] = useState<PerMinuteRow[]>([]);
  const [stt, setStt] = useState<PerMinuteRow[]>([]);
  const [usdPerChar, setUsdPerChar] = useState('');
  const [margins, setMargins] = useState<Record<keyof AiMargins, string>>({
    chat: '', tts: '', stt: '', realtime: '',
  });

  useEffect(() => {
    if (!data) return;
    setRealtime(Object.entries(data.realtime).map(([model, values]) => ({
      model,
      values: REALTIME_PRICE_FIELDS.reduce(
        (acc, field) => ({ ...acc, [field.key]: String(values[field.key] ?? 0) }),
        {} as Record<string, string>,
      ),
    })));
    setChat(Object.entries(data.chat).map(([model, v]) => ({
      model, input: String(v.input), output: String(v.output),
    })));
    setTts(Object.entries(data.tts).map(([model, v]) => ({ model, usdPerMin: String(v.usdPerMin) })));
    setStt(Object.entries(data.stt).map(([model, v]) => ({ model, usdPerMin: String(v.usdPerMin) })));
    setUsdPerChar(String(data.elevenlabs.usdPerChar));
    setMargins({
      chat: String(data.margins.chat),
      tts: String(data.margins.tts),
      stt: String(data.margins.stt),
      realtime: String(data.margins.realtime),
    });
  }, [data]);

  /** Wiersz bez nazwy modelu jest pomijany, a nie zgłaszany jako błąd: to zwykle świeżo dodany, jeszcze niewypełniony. */
  const payload: AiPrices | null = useMemo(() => {
    if (!data) return null;

    const named = <T extends { model: string }>(rows: T[]): T[] => rows.filter((r) => r.model.trim() !== '');

    return {
      realtime: named(realtime).reduce((acc, row) => ({
        ...acc,
        [row.model.trim()]: REALTIME_PRICE_FIELDS.reduce(
          (fields, field) => ({ ...fields, [field.key]: toNumber(row.values[field.key]) }),
          {} as Record<string, number>,
        ),
      }), {}),
      chat: named(chat).reduce((acc, row) => ({
        ...acc,
        [row.model.trim()]: { input: toNumber(row.input), output: toNumber(row.output) },
      }), {}),
      tts: named(tts).reduce((acc, row) => ({
        ...acc, [row.model.trim()]: { usdPerMin: toNumber(row.usdPerMin) },
      }), {}),
      stt: named(stt).reduce((acc, row) => ({
        ...acc, [row.model.trim()]: { usdPerMin: toNumber(row.usdPerMin) },
      }), {}),
      elevenlabs: { usdPerChar: toNumber(usdPerChar) },
      margins: {
        chat: toNumber(margins.chat),
        tts: toNumber(margins.tts),
        stt: toNumber(margins.stt),
        realtime: toNumber(margins.realtime),
      },
    } as AiPrices;
  }, [data, realtime, chat, tts, stt, usdPerChar, margins]);

  const hasInvalid = useMemo(() => {
    const badRealtime = realtime.some((row) => row.model.trim() !== ''
      && REALTIME_PRICE_FIELDS.some((field) => !isValidPrice(row.values[field.key])));
    const badChat = chat.some((row) => row.model.trim() !== ''
      && (!isValidPrice(row.input) || !isValidPrice(row.output)));
    const badPerMinute = [...tts, ...stt].some((row) => row.model.trim() !== '' && !isValidPrice(row.usdPerMin));
    const badMargins = (Object.keys(margins) as (keyof AiMargins)[]).some((key) => !isValidMargin(margins[key]));
    return badRealtime || badChat || badPerMinute || badMargins || !isValidPrice(usdPerChar);
  }, [realtime, chat, tts, stt, margins, usdPerChar]);

  const handleUpdate = async (): Promise<void> => {
    if (!payload || hasInvalid) return;
    try {
      await updatePrices(payload);
      await queryClient.invalidateQueries({ queryKey: [AI_PRICES_QUERY_KEY] });
      showSuccess('Cennik zaktualizowany');
    } catch (e: any) {
      showError(e?.message || 'Nie udało się zapisać cennika');
    }
  };

  if (isLoading) return <Spinner global />;
  if (error || !data) {
    return (
      <PageWrapper>
        <ContentLoadError onRefreshClicked={() => { refetch(); }} message={error?.message} />
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <PageTitle
        title="Cennik AI"
        actions={(
          <Button
            color="primary"
            size="sm"
            startContent={<RefreshCw size={14} />}
            isDisabled={hasInvalid || isSaving}
            onPress={handleUpdate}
          >
            {isSaving ? 'Zapisywanie...' : 'Aktualizuj cennik'}
          </Button>
        )}
      />

      <p className="text-sm text-default-500 mt-2 mb-4 max-w-[820px]">
        Stawki dostawców bez marży, dokładnie jak w ich cennikach. Po nich liczone jest zużycie
        wszystkich organizacji. Marża jest osobno, więc po podwyżce u dostawcy widać, ile z kwoty
        jest kosztem, a ile narzutem.
      </p>

      {!data.configured && (
        <Alert
          className="mb-4"
          color="warning"
          title="Cennik nie został jeszcze zapisany"
          description="Poniżej widnieją stawki, po których system liczy dzisiaj. Kliknij Aktualizuj cennik, żeby przenieść je do bazy i móc nimi zarządzać stąd."
        />
      )}

      {data.configured && data.updatedAt && (
        <p className="text-tiny text-default-500 mb-4">
          Ostatnia aktualizacja:
          {' '}
          {new Date(data.updatedAt).toLocaleString('pl-PL')}
        </p>
      )}

      <Tabs aria-label="Sekcje cennika">
        <Tab key="realtime" title="Rozmowy live">
          <Section
            title="Modele rozmów live"
            description="Ceny w dolarach za milion tokenów. Model bez wpisu w cenniku ma rozmowy live wyłączone."
            onAdd={() => setRealtime((prev) => [...prev, {
              model: '',
              values: REALTIME_PRICE_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: '0' }), {}),
            }])}
          >
            {realtime.map((row, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={`realtime-${index}`} className="flex flex-col gap-3 pb-4 border-b border-default-200 last:border-0">
                <div className="flex items-end gap-2">
                  <Input
                    label="Model"
                    value={row.model}
                    onValueChange={(v) => setRealtime((prev) => prev.map((r, i) => (i === index ? { ...r, model: v } : r)))}
                    className="max-w-[320px]"
                  />
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    color="danger"
                    onPress={() => setRealtime((prev) => prev.filter((_, i) => i !== index))}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {REALTIME_PRICE_FIELDS.map((field) => (
                    <Input
                      key={field.key}
                      label={field.label}
                      value={row.values[field.key] ?? ''}
                      onValueChange={(v) => setRealtime((prev) => prev.map((r, i) => (
                        i === index ? { ...r, values: { ...r.values, [field.key]: v } } : r
                      )))}
                      startContent={<span className="text-default-400 text-sm">$</span>}
                      isInvalid={row.model.trim() !== '' && !isValidPrice(row.values[field.key] ?? '')}
                      errorMessage="Podaj liczbę nieujemną"
                    />
                  ))}
                </div>
              </div>
            ))}
          </Section>
        </Tab>

        <Tab key="chat" title="Wiadomości tekstowe">
          <Section
            title="Modele tekstowe"
            description="Ceny w dolarach za milion tokenów, osobno za wejście i wyjście."
            onAdd={() => setChat((prev) => [...prev, { model: '', input: '0', output: '0' }])}
          >
            {chat.map((row, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={`chat-${index}`} className="flex items-end gap-3">
                <Input
                  label="Model"
                  value={row.model}
                  onValueChange={(v) => setChat((prev) => prev.map((r, i) => (i === index ? { ...r, model: v } : r)))}
                  className="max-w-[320px]"
                />
                <Input
                  label="Wejście"
                  value={row.input}
                  onValueChange={(v) => setChat((prev) => prev.map((r, i) => (i === index ? { ...r, input: v } : r)))}
                  startContent={<span className="text-default-400 text-sm">$</span>}
                  isInvalid={row.model.trim() !== '' && !isValidPrice(row.input)}
                  errorMessage="Podaj liczbę nieujemną"
                  className="max-w-[200px]"
                />
                <Input
                  label="Wyjście"
                  value={row.output}
                  onValueChange={(v) => setChat((prev) => prev.map((r, i) => (i === index ? { ...r, output: v } : r)))}
                  startContent={<span className="text-default-400 text-sm">$</span>}
                  isInvalid={row.model.trim() !== '' && !isValidPrice(row.output)}
                  errorMessage="Podaj liczbę nieujemną"
                  className="max-w-[200px]"
                />
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  color="danger"
                  onPress={() => setChat((prev) => prev.filter((_, i) => i !== index))}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            ))}
          </Section>
        </Tab>

        <Tab key="voice" title="Głosówki">
          <div className="flex flex-col gap-5">
            <Section
              title="Synteza mowy"
              description="Cena w dolarach za minutę wygenerowanego audio."
              onAdd={() => setTts((prev) => [...prev, { model: '', usdPerMin: '0' }])}
            >
              {tts.map((row, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={`tts-${index}`} className="flex items-end gap-3">
                  <Input
                    label="Model"
                    value={row.model}
                    onValueChange={(v) => setTts((prev) => prev.map((r, i) => (i === index ? { ...r, model: v } : r)))}
                    className="max-w-[320px]"
                  />
                  <Input
                    label="Cena za minutę"
                    value={row.usdPerMin}
                    onValueChange={(v) => setTts((prev) => prev.map((r, i) => (i === index ? { ...r, usdPerMin: v } : r)))}
                    startContent={<span className="text-default-400 text-sm">$</span>}
                    isInvalid={row.model.trim() !== '' && !isValidPrice(row.usdPerMin)}
                    errorMessage="Podaj liczbę nieujemną"
                    className="max-w-[220px]"
                  />
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    color="danger"
                    onPress={() => setTts((prev) => prev.filter((_, i) => i !== index))}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              ))}
            </Section>

            <Section
              title="Transkrypcja"
              description="Cena w dolarach za minutę nagrania gościa."
              onAdd={() => setStt((prev) => [...prev, { model: '', usdPerMin: '0' }])}
            >
              {stt.map((row, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={`stt-${index}`} className="flex items-end gap-3">
                  <Input
                    label="Model"
                    value={row.model}
                    onValueChange={(v) => setStt((prev) => prev.map((r, i) => (i === index ? { ...r, model: v } : r)))}
                    className="max-w-[320px]"
                  />
                  <Input
                    label="Cena za minutę"
                    value={row.usdPerMin}
                    onValueChange={(v) => setStt((prev) => prev.map((r, i) => (i === index ? { ...r, usdPerMin: v } : r)))}
                    startContent={<span className="text-default-400 text-sm">$</span>}
                    isInvalid={row.model.trim() !== '' && !isValidPrice(row.usdPerMin)}
                    errorMessage="Podaj liczbę nieujemną"
                    className="max-w-[220px]"
                  />
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    color="danger"
                    onPress={() => setStt((prev) => prev.filter((_, i) => i !== index))}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              ))}
            </Section>

            <Card shadow="sm">
              <CardHeader>
                <div>
                  <p className="text-medium font-semibold">ElevenLabs</p>
                  <p className="text-tiny text-default-500 mt-1">
                    Rozliczenie per znak wysłany do syntezy, nie per minuta.
                  </p>
                </div>
              </CardHeader>
              <Divider />
              <CardBody>
                <Input
                  label="Cena za znak"
                  value={usdPerChar}
                  onValueChange={setUsdPerChar}
                  startContent={<span className="text-default-400 text-sm">$</span>}
                  isInvalid={!isValidPrice(usdPerChar)}
                  errorMessage="Podaj liczbę nieujemną"
                  className="max-w-[260px]"
                />
              </CardBody>
            </Card>
          </div>
        </Tab>

        <Tab key="margins" title="Marże">
          <Card shadow="sm">
            <CardHeader>
              <div>
                <p className="text-medium font-semibold">Narzut nad kosztem dostawcy</p>
                <p className="text-tiny text-default-500 mt-1 max-w-[620px]">
                  Mnożnik stosowany przy naliczaniu zużycia. 1.25 oznacza 25 procent narzutu.
                  Dla rozmów live to wartość domyślna: organizacja z własną marżą w ustawieniach
                  kanału ma pierwszeństwo.
                </p>
              </div>
            </CardHeader>
            <Divider />
            <CardBody className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(Object.keys(MARGIN_LABELS) as (keyof AiMargins)[]).map((key) => (
                <Input
                  key={key}
                  label={MARGIN_LABELS[key]}
                  value={margins[key]}
                  onValueChange={(v) => setMargins((prev) => ({ ...prev, [key]: v }))}
                  isInvalid={!isValidMargin(margins[key])}
                  errorMessage="Podaj wartość z zakresu 1 do 5"
                />
              ))}
            </CardBody>
          </Card>
        </Tab>
      </Tabs>
    </PageWrapper>
  );
};
